import React, { useState, useRef } from 'react';
import MobileNoteInput from './MobileNoteInput';
import './MobileProfile.css';

function MobileProfile({
  isOpen,
  onClose,
  donorName,
  narrative,
  timelineEvents = [],
  donorDetails = {},
  onAddNote,
  onSubmitNote
}) {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const profileRef = useRef(null);

  // Extract initials from name
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Generate a consistent color based on name
  const getAvatarGradient = (name) => {
    const colors = [
      ['#a78bfa', '#7c3aed'], // purple
      ['#f472b6', '#db2777'], // pink
      ['#60a5fa', '#2563eb'], // blue
      ['#4ade80', '#16a34a'], // green
      ['#fbbf24', '#d97706'], // yellow
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return `linear-gradient(135deg, ${colors[index][0]} 0%, ${colors[index][1]} 100%)`;
  };

  // Handle drag to close
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
    setTouchCurrent(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setTouchCurrent(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging || !touchStart || !touchCurrent) {
      setIsDragging(false);
      return;
    }

    const dragDistance = touchCurrent - touchStart;
    if (dragDistance > 100) {
      onClose();
    }

    setIsDragging(false);
    setTouchStart(null);
    setTouchCurrent(null);
  };

  // Calculate drag offset for animation
  const dragOffset = isDragging && touchStart && touchCurrent
    ? Math.max(0, touchCurrent - touchStart)
    : 0;

  // Extract key details from narrative using simple patterns
  const extractKeyDetails = (narrativeText) => {
    if (!narrativeText) return [];
    const details = [];
    const text = narrativeText.toLowerCase();

    // Interest patterns
    const interests = ['arts', 'education', 'music', 'sports', 'athletics', 'science', 'research',
                       'scholarship', 'building', 'campus', 'library', 'technology', 'stem',
                       'youth', 'children', 'health', 'medical', 'environmental', 'sustainability'];
    interests.forEach(interest => {
      if (text.includes(interest)) {
        details.push({ label: interest.charAt(0).toUpperCase() + interest.slice(1), type: 'interest' });
      }
    });

    // Family patterns - look for family member mentions
    const familyPatterns = [
      /(?:wife|husband|spouse)\s+(\w+)/i,
      /(?:daughter|son)\s+(\w+)/i,
      /(?:brother|sister)\s+(\w+)/i,
    ];
    familyPatterns.forEach(pattern => {
      const match = narrativeText.match(pattern);
      if (match) {
        const relation = pattern.toString().includes('wife') ? 'Wife' :
                        pattern.toString().includes('husband') ? 'Husband' :
                        pattern.toString().includes('daughter') ? 'Daughter' :
                        pattern.toString().includes('son') ? 'Son' :
                        pattern.toString().includes('brother') ? 'Brother' : 'Sister';
        details.push({ label: `${relation}: ${match[1]}`, type: 'family' });
      }
    });

    // Giving patterns
    const amountMatch = narrativeText.match(/\$[\d,]+[kKmM]?/);
    if (amountMatch) {
      details.push({ label: amountMatch[0], type: 'giving' });
    }

    // Next steps patterns
    if (text.includes('follow up') || text.includes('follow-up')) {
      details.push({ label: 'Follow up needed', type: 'next' });
    }
    if (text.includes('send') || text.includes('email')) {
      details.push({ label: 'Send info', type: 'next' });
    }
    if (text.includes('meeting') || text.includes('schedule')) {
      details.push({ label: 'Schedule meeting', type: 'next' });
    }

    // Limit to 6 items and remove duplicates
    const unique = details.filter((item, index, self) =>
      index === self.findIndex(t => t.label === item.label)
    );
    return unique.slice(0, 6);
  };

  // Parse timeline events by type
  const getEventType = (event) => {
    const text = (event.event || '').toLowerCase();
    if (text.includes('meeting') || text.includes('coffee') || text.includes('lunch') || text.includes('dinner')) return 'meeting';
    if (text.includes('call') || text.includes('phone')) return 'call';
    if (text.includes('gala') || text.includes('event') || text.includes('party')) return 'event';
    return 'research';
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'meeting': return '#4ade80';
      case 'call': return '#60a5fa';
      case 'event': return '#a78bfa';
      default: return '#666';
    }
  };

  // Format relative date
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';

    // If it's a natural language date without a year, just display it as-is
    const hasYear = /\b20\d{2}\b/.test(dateStr) || /^\d{4}-\d{2}-\d{2}/.test(dateStr);
    if (!hasYear) return dateStr;

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    // Sanity check: if the parsed year is more than 2 years off, something went wrong
    const now = new Date();
    const yearDiff = now.getFullYear() - date.getFullYear();
    if (yearDiff > 2 || yearDiff < -2) return dateStr;

    const diffMs = now - date;
    const diffDays = Math.floor(Math.abs(diffMs) / 86400000);
    const isFuture = diffMs < 0;

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return isFuture ? 'Tomorrow' : 'Yesterday';
    if (diffDays < 7) return isFuture ? `In ${diffDays}d` : `${diffDays}d ago`;
    if (diffDays < 30) return isFuture ? `In ${Math.floor(diffDays / 7)}w` : `${Math.floor(diffDays / 7)}w ago`;
    return isFuture ? `In ${Math.floor(diffDays / 30)}mo` : `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Get stats from donorDetails
  const stats = {
    amount: donorDetails?.amount || 'Unknown',
    meetings: timelineEvents.filter(e => getEventType(e) === 'meeting').length || 0,
    lastContact: donorDetails?.lastContact || 'Unknown'
  };

  // Format lastContact for display
  const formatLastContact = (lc) => {
    if (!lc || lc === 'Unknown') return '—';
    // If it's already formatted like "2 days ago", extract just the number part
    const match = lc.match(/(\d+)\s*(day|week|month|d|w|mo)/i);
    if (match) {
      const num = match[1];
      const unit = match[2].charAt(0).toLowerCase();
      return `${num}${unit}`;
    }
    return lc;
  };

  // Extract key details from narrative
  const keyDetails = extractKeyDetails(narrative?.content || '');

  // Check for pending tasks
  const hasPendingTask = donorDetails?.nextTask && donorDetails.nextTask !== 'No tasks';

  if (!isOpen) return null;

  return (
    <div className="mobile-profile-overlay">
      <div
        className="mobile-profile"
        ref={profileRef}
        style={{ transform: `translateY(${dragOffset}px)` }}
      >
        {/* Drag handle */}
        <div
          className="mobile-profile-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="handle-pill" />
        </div>

        {/* Scrollable content */}
        <div className="mobile-profile-content">

          {/* Profile header */}
          <div className="mobile-profile-header">
            <div
              className="mobile-profile-avatar"
              style={{ background: getAvatarGradient(donorName) }}
            >
              {getInitials(donorName)}
            </div>
            <h1 className="mobile-profile-name">{donorName}</h1>
            {donorDetails?.title && (
              <p className="mobile-profile-title">{donorDetails.title}</p>
            )}
            {donorDetails?.organization && (
              <p className="mobile-profile-org">{donorDetails.organization}</p>
            )}
          </div>

          {/* Quick actions */}
          <div className="mobile-profile-actions">
            <div className="profile-action">
              <div className="profile-action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <span>Email</span>
            </div>
            <div className="profile-action">
              <div className="profile-action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <span>Call</span>
            </div>
            <div className="profile-action">
              <div className="profile-action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="1"/>
                  <circle cx="19" cy="12" r="1"/>
                  <circle cx="5" cy="12" r="1"/>
                </svg>
              </div>
              <span>More</span>
            </div>
          </div>

          {/* Stats row */}
          <div className="mobile-profile-stats">
            <div className="profile-stat">
              <div className="profile-stat-value green">{stats.amount}</div>
              <div className="profile-stat-label">Prospect</div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-value">{stats.meetings}</div>
              <div className="profile-stat-label">Meetings</div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-value">{formatLastContact(stats.lastContact)}</div>
              <div className="profile-stat-label">Last Seen</div>
            </div>
          </div>

          {/* The Story */}
          <div className="mobile-profile-section">
            <div className="section-label">The Story</div>
            <p className="profile-story">
              {narrative?.content || `No story yet. Add notes about ${donorName} to build their profile.`}
            </p>
          </div>

          {/* Key Details */}
          {keyDetails.length > 0 && (
            <div className="mobile-profile-section">
              <div className="section-label">Key Details</div>
              <div className="key-details-tags">
                {keyDetails.map((detail, idx) => (
                  <span
                    key={idx}
                    className={`key-detail-tag ${detail.type}`}
                  >
                    {detail.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          {timelineEvents.length > 0 && (
            <div className="mobile-profile-section">
              <div className="section-label">Timeline</div>
              <div className="profile-timeline">
                <div className="timeline-line" />
                {timelineEvents.map((event, idx) => {
                  const type = getEventType(event);
                  return (
                    <div key={idx} className="timeline-entry">
                      <div
                        className="timeline-dot"
                        style={{ background: getEventColor(type) }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-title">{event.event}</span>
                          <span className="timeline-date">{formatRelativeDate(event.date)}</span>
                        </div>
                        {event.detail && (
                          <p className="timeline-detail">{event.detail}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* To Do */}
          {hasPendingTask && (
            <div className="mobile-profile-section">
              <div className="section-label">To Do</div>
              <div className="profile-todo">
                <div className="todo-checkbox" />
                <div className="todo-content">
                  <div className="todo-text">{donorDetails.nextTask}</div>
                  {donorDetails.nextMeeting && donorDetails.nextMeeting !== 'Not scheduled' && (
                    <div className="todo-due">{formatRelativeDate(donorDetails.nextMeeting)}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Spacer for bottom button */}
          <div style={{ height: '100px' }} />

        </div>

        {/* Fixed bottom action */}
        <div className="mobile-profile-bottom">
          <button
            className="profile-add-note-btn"
            onClick={() => setShowNoteInput(true)}
          >
            Add Note About {donorName?.split(' ')[0]}
          </button>
        </div>
      </div>

      {/* Note input flow */}
      <MobileNoteInput
        isOpen={showNoteInput}
        onClose={() => setShowNoteInput(false)}
        donorName={donorName}
        onSubmit={async (message) => {
          if (onSubmitNote) {
            const result = await onSubmitNote(message);
            return result;
          }
          return { captured: [{ type: 'logged', text: 'Note saved' }] };
        }}
      />
    </div>
  );
}

export default MobileProfile;
